import React, {
  AllHTMLAttributes,
  DragEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  useRef,
  useState,
} from 'react';

import axios from 'axios';

import { UploadedFile } from '../../../../api-client';
import { createUploadManager } from '../../../../di';
import { useErrorCallback, useNotifier } from '../../../../hooks';
import { cn } from '../../../../utils';
import { CrossIcon } from '../../../common';

import './IdentificationFileUpload.scoped.scss';

interface Props extends AllHTMLAttributes<HTMLDivElement> {
  prefix: string;
  text: string;
  files: Array<UploadedFile>;
  onUploaded: (files: Array<UploadedFile>) => void;
}

const IdentificationFileUpload: FC<Props> = ({ prefix, text, files, onUploaded, ...props }) => {
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const notifier = useNotifier();
  const handleError = useErrorCallback();

  const uploadFile = async (file: File | undefined) => {
    if (!file || loading) {
      return;
    }
    setLoading(true);
    try {
      const manager = await createUploadManager();
      const uploadedFile = await manager.uploadFile(file);
      onUploaded([uploadedFile]);
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        notifier.createError('Файл слишком большой. Загрузите файл размером до 4 Мб');
        return;
      }
      await handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await uploadFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragOver(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    await uploadFile(e.dataTransfer.files?.[0]);
  };

  const openFileDialog = () => {
    if (!loading) {
      inputRef.current?.click();
    }
  };

  const handleBlockClick = (e: MouseEvent<HTMLDivElement>) => {
    // The label opens the dialog itself, the delete button must not open it
    if ((e.target as HTMLElement).closest('label, input, .identification-file-upload__delete')) {
      return;
    }
    openFileDialog();
  };

  const handleBlockKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      openFileDialog();
    }
  };

  //TODO: ADD A REAL REMOVING FILE METHOD
  const removeFile = async () => {
    onUploaded([]);
  };

  const renderFiles = () => {
    if (loading) {
      return <div className="identification-file-upload__file-placeholder">Загрузка...</div>;
    }
    if (files.length === 0) {
      return <div className="identification-file-upload__file-placeholder">Файлы не выбраны</div>;
    }
    const renderedFiles = () => {
      return files.map((file: UploadedFile, index) => {
        return (
          <li className="identification-file-upload__element" key={index}>
            <div className="identification-file-upload__delete" onClick={removeFile}>
              <CrossIcon preset="default" size={16} title="Удалить файл" />
            </div>
            <div>{file.name}</div>
          </li>
        );
      });
    };

    return <ul className="identification-file-upload__list">{renderedFiles()}</ul>;
  };

  return (
    <div {...props} className={cn('identification-file-upload', props.className)}>
      <div className="identification-file-upload__text">{text}</div>
      <div
        className={cn(
          'identification-file-upload__block',
          dragOver ? 'identification-file-upload__block--drag' : undefined
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBlockClick}
        onKeyDown={handleBlockKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="identification-file-upload__placeholder">
          Перетащите файл сюда или <label htmlFor={`upload-${prefix}`}>выберите файл</label>
        </div>
        <div className="identification-file-upload__file">
          <input
            id={`upload-${prefix}`}
            ref={inputRef}
            type="file"
            accept="image/*,.pdf"
            disabled={loading}
            onChange={handleChange}
          />
          {renderFiles()}
        </div>
      </div>
    </div>
  );
};

export default IdentificationFileUpload;
